import { useState, useCallback } from 'react'
import { useApiFetch, useApiMutation } from '../../hooks/useApiFetch.js'
import { useUIStore } from '../../stores/uiStore.js'
import { Badge, Toggle, Table, Alert, Tabs, type TableColumn, type TabItem } from '../../components/ui/index.js'
import styles from './AdminAIModels.module.css'

// ── Image Model ──

interface ImageModelSetting {
  id: string
  name: string
  provider: string
  providerType: string
  costPerImage: number
  description: string
  facePreservation: string
  speed: string
  providerModel: string
  supportsReferenceImage: boolean
  modelType: 'image'
  enabled: boolean
  isRecommended: boolean
  isDefault: boolean
  sortOrder: number
  updatedAt: string | null
}

// ── Text Model ──

interface TextModelSetting {
  id: string
  name: string
  provider: string
  providerType: string
  costPer1kInputTokens: number
  costPer1kOutputTokens: number
  maxContextTokens: number
  providerModel: string
  description: string
  modelType: 'text'
  enabled: boolean
  isDefault: boolean
  sortOrder: number
  updatedAt: string | null
}

interface AIModelsResponse {
  imageModels: ImageModelSetting[]
  textModels: TextModelSetting[]
}

const SPEED_COLORS: Record<string, 'green' | 'yellow' | 'red'> = {
  fast: 'green',
  medium: 'yellow',
  slow: 'red',
}

const FACE_COLORS: Record<string, 'green' | 'blue' | 'yellow' | 'red'> = {
  excellent: 'green',
  good: 'blue',
  fair: 'yellow',
  poor: 'red',
}

const TAB_ITEMS: TabItem[] = [
  { key: 'image', label: '이미지 모델' },
  { key: 'text', label: '텍스트 모델' },
]

export function AdminAIModels() {
  const { addToast } = useUIStore()
  const [activeTab, setActiveTab] = useState('image')

  const { data, isLoading, error } = useApiFetch<AIModelsResponse>(
    '/api/admin/ai-models'
  )

  const { trigger, isLoading: patchLoading } = useApiMutation<AIModelsResponse, Record<string, unknown>>(
    '/api/admin/ai-models',
    'PATCH',
    {
      onMutate: (body) => ({
        queryKey: ['/api/admin/ai-models'],
        updater: (old: AIModelsResponse) => {
          const b = body as Record<string, unknown>
          return {
            imageModels: old.imageModels.map((m) =>
              m.id === b.modelId ? { ...m, ...b } : (b.isDefault && b.modelType === 'image' ? { ...m, isDefault: false } : m)
            ),
            textModels: old.textModels.map((m) =>
              m.id === b.modelId ? { ...m, ...b } : (b.isDefault && b.modelType === 'text' ? { ...m, isDefault: false } : m)
            ),
          }
        },
      }),
    },
  )

  const handleToggle = useCallback(async (
    modelId: string,
    modelType: 'image' | 'text',
    field: 'enabled' | 'isRecommended' | 'isDefault',
    value: boolean,
  ) => {
    const result = await trigger({ modelId, modelType, [field]: value })
    if (result) {
      addToast({ type: 'success', message: '설정이 변경되었습니다.' })
    } else {
      addToast({ type: 'error', message: '설정 변경에 실패했습니다.' })
    }
  }, [trigger, addToast])

  const imageColumns: TableColumn<ImageModelSetting>[] = [
    { key: 'sortOrder', label: '#', render: (row) => String(row.sortOrder), width: '40px' },
    {
      key: 'name',
      label: '모델',
      render: (row) => (
        <div>
          <div className={styles.modelName}>{row.name}</div>
          <div className={styles.modelId}>{row.id}</div>
        </div>
      ),
    },
    { key: 'provider', label: '프로바이더', width: '90px' },
    { key: 'costPerImage', label: '비용', render: (row) => `$${row.costPerImage.toFixed(2)}`, align: 'right' as const, width: '70px' },
    {
      key: 'speed',
      label: '속도',
      render: (row) => <Badge color={SPEED_COLORS[row.speed] ?? 'gray'}>{row.speed}</Badge>,
      width: '80px',
    },
    {
      key: 'facePreservation',
      label: '얼굴 보존',
      render: (row) => <Badge color={FACE_COLORS[row.facePreservation] ?? 'gray'}>{row.facePreservation}</Badge>,
      width: '100px',
    },
    {
      key: 'supportsReferenceImage',
      label: '참조',
      render: (row) => row.supportsReferenceImage ? <Badge color="green">O</Badge> : <Badge color="gray">X</Badge>,
      width: '60px',
    },
    {
      key: 'enabled',
      label: '활성',
      render: (row) => (
        <Toggle
          checked={row.enabled}
          onChange={(checked) => handleToggle(row.id, 'image', 'enabled', checked)}
          disabled={patchLoading}
          size="sm"
        />
      ),
      width: '70px',
    },
    {
      key: 'isRecommended',
      label: '추천',
      render: (row) => (
        <Toggle
          checked={row.isRecommended}
          onChange={(checked) => handleToggle(row.id, 'image', 'isRecommended', checked)}
          disabled={patchLoading}
          size="sm"
        />
      ),
      width: '70px',
    },
    {
      key: 'isDefault',
      label: '기본',
      render: (row) => (
        <Toggle
          checked={row.isDefault}
          onChange={(checked) => handleToggle(row.id, 'image', 'isDefault', checked)}
          disabled={patchLoading}
          size="sm"
        />
      ),
      width: '70px',
    },
    { key: 'description', label: '설명' },
  ]

  const textColumns: TableColumn<TextModelSetting>[] = [
    { key: 'sortOrder', label: '#', render: (row) => String(row.sortOrder), width: '40px' },
    {
      key: 'name',
      label: '모델',
      render: (row) => (
        <div>
          <div className={styles.modelName}>{row.name}</div>
          <div className={styles.modelId}>{row.id}</div>
        </div>
      ),
    },
    { key: 'provider', label: '프로바이더', width: '90px' },
    {
      key: 'costPer1kInputTokens',
      label: 'Input 비용',
      render: (row) => `$${row.costPer1kInputTokens.toFixed(4)}/1k`,
      align: 'right' as const,
      width: '120px',
    },
    {
      key: 'costPer1kOutputTokens',
      label: 'Output 비용',
      render: (row) => `$${row.costPer1kOutputTokens.toFixed(4)}/1k`,
      align: 'right' as const,
      width: '120px',
    },
    {
      key: 'maxContextTokens',
      label: 'Max Context',
      render: (row) => `${(row.maxContextTokens / 1000).toFixed(0)}K`,
      align: 'right' as const,
      width: '100px',
    },
    {
      key: 'enabled',
      label: '활성',
      render: (row) => (
        <Toggle
          checked={row.enabled}
          onChange={(checked) => handleToggle(row.id, 'text', 'enabled', checked)}
          disabled={patchLoading}
          size="sm"
        />
      ),
      width: '70px',
    },
    {
      key: 'isDefault',
      label: '기본',
      render: (row) => (
        <Toggle
          checked={row.isDefault}
          onChange={(checked) => handleToggle(row.id, 'text', 'isDefault', checked)}
          disabled={patchLoading}
          size="sm"
        />
      ),
      width: '70px',
    },
    { key: 'description', label: '설명' },
  ]

  return (
    <div className={styles.root}>
      <Tabs items={TAB_ITEMS} value={activeTab} onChange={setActiveTab} variant="card" size="sm" />

      {error && <Alert variant="danger">{error}</Alert>}

      {activeTab === 'image' && (
        <Table<ImageModelSetting>
          columns={imageColumns}
          data={data?.imageModels ?? []}
          rowKey={(row) => row.id}
          isLoading={isLoading}
        />
      )}

      {activeTab === 'text' && (
        <Table<TextModelSetting>
          columns={textColumns}
          data={data?.textModels ?? []}
          rowKey={(row) => row.id}
          isLoading={isLoading}
        />
      )}
    </div>
  )
}
